/**
 * Mobile League Transactions — CD delivery `League Transactions.dc.html`.
 * Mock (its own <p> is the spec): "Summary quick-filter tiles (Total · Adds · Drops ·
 * Trades) + day range + search → flush activity rows (color dot · team verb player ·
 * date/salary/notes · type badge). Flat, no curves, no scrollbars."
 *
 * Layout (per the mock markup): LeagueChrome (active transactions, parent) → summary
 * quick-filter tiles (1px-gap flush, gold-underline active) → filter chip row (Days ·
 * Teams · Types · search) → flush activity list (.nobar) → BottomNav (app). No section
 * labels in the mock, so no MobileSectionHeader here. Tokens only, zero hex; flush, no
 * rounded cards; hidden scrollbars; tabular-nums on the numeric tile vals + row meta.
 *
 * Reuse (Rule 11): the canonical transactions endpoint (leaguesAPI.getTransactions) +
 * the TYPE_CONFIG color/label/verb map + the flush activity-row treatment + the player
 * modal. New: the chip filter row + team filtering (derived from the feed).
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';
import { MobilePlayerModal, MobilePlayerCard } from '../components/player';

const C = dynastyTheme.tokens.colors;

const TYPE_CONFIG = {
  free_agent_claim:   { color: C.success, label: 'Add',    verb: 'added' },
  fa_acquisition:     { color: C.success, label: 'Add',    verb: 'added' },
  commissioner_add:   { color: C.success, label: 'Add',    verb: 'added' },
  draft_pick:         { color: C.success, label: 'Draft',  verb: 'drafted' },
  minor_draft_pick:   { color: C.success, label: 'Draft',  verb: 'drafted' },
  autodraft:          { color: C.success, label: 'Draft',  verb: 'drafted' },
  minor_promotion:    { color: C.success, label: 'Promo',  verb: 'promoted' },
  keeper_selection:   { color: C.success, label: 'Keeper', verb: 'kept' },
  release:            { color: C.error,   label: 'Drop',   verb: 'released' },
  offseason_reset:    { color: C.error,   label: 'Reset',  verb: 'released' },
  draft_pick_reversal:{ color: C.error,   label: 'Undo',   verb: 'reversed' },
  trade:              { color: C.primary, label: 'Trade',  verb: 'traded' },
  contract_change:    { color: C.warning, label: 'Move',   verb: 'moved' },
  roster_move:        { color: C.warning, label: 'Move',   verb: 'moved' },
};
const getConfig = (type) => TYPE_CONFIG[type] || { color: C.neutral[500], label: 'Other', verb: 'transacted' };

const ADD_TYPES = ['free_agent', 'free_agent_claim', 'fa_acquisition', 'commissioner_add', 'draft_pick', 'minor_draft_pick', 'autodraft', 'minor_promotion', 'keeper_selection'];
const DROP_TYPES = ['release', 'offseason_reset'];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const DAYS_FILTER_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 365, label: 'All time' },
];

// Chip-styled control (matches the mock's filter chips: flat dark, hairline border,
// 3px radius, 10px/700). Native <select> arrow kept for the ▾ affordance.
const CHIP = {
  fontSize: 10, fontWeight: 700, color: C.textHi, background: C.ground.screen,
  border: `1px solid ${C.border}`, borderRadius: 3, padding: '6px 9px',
  outline: 'none', cursor: 'pointer', maxWidth: 120,
};

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
);

function MobileLeagueTransactions({ leagueId }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickFilter, setQuickFilter] = useState('total'); // total | adds | drops | trades
  const [teamFilter, setTeamFilter] = useState('');
  const [daysFilter, setDaysFilter] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');
  const listRef = useRef(null);
  const [listHeight, setListHeight] = useState(400);

  // Player modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [activePlayerId, setActivePlayerId] = useState(null);

  // Measure available space → tiles + filters stay put, only the list scrolls (mock).
  useEffect(() => {
    const measure = () => {
      if (listRef.current) {
        const rect = listRef.current.getBoundingClientRect();
        const available = window.innerHeight - rect.top - 70 - 12;
        setListHeight(Math.max(200, available));
      }
    };
    const timer = setTimeout(measure, 200);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(timer); window.removeEventListener('resize', measure); };
  }, [loading]);

  useEffect(() => {
    if (leagueId) fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, daysFilter]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await leaguesAPI.getTransactions(leagueId, { limit: 500, days_back: daysFilter });
      const txns = res?.transactions || res?.data || [];
      setTransactions(Array.isArray(txns) ? txns : []);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Team options derived from the feed (mock's "All teams ▾"; no extra endpoint).
  const teamOptions = useMemo(() => {
    const set = new Set();
    transactions.forEach(t => [t.to_team_name, t.from_team_name].forEach(n => {
      if (n && n !== 'Free Agency') set.add(n);
    }));
    return Array.from(set).sort();
  }, [transactions]);

  // Quick filter (tiles ↔ Types chip, shared state) + team + search.
  const filtered = useMemo(() => {
    let result = transactions;
    if (quickFilter === 'adds') result = result.filter(t => ADD_TYPES.includes(t.transaction_type));
    else if (quickFilter === 'drops') result = result.filter(t => DROP_TYPES.includes(t.transaction_type));
    else if (quickFilter === 'trades') result = result.filter(t => t.transaction_type === 'trade');
    if (teamFilter) result = result.filter(t => t.to_team_name === teamFilter || t.from_team_name === teamFilter);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(t =>
        (t.player_name || '').toLowerCase().includes(term) ||
        (t.from_team_name || '').toLowerCase().includes(term) ||
        (t.to_team_name || '').toLowerCase().includes(term));
    }
    return result;
  }, [transactions, quickFilter, teamFilter, searchTerm]);

  const counts = useMemo(() => {
    const c = { total: transactions.length, adds: 0, drops: 0, trades: 0 };
    transactions.forEach(t => {
      const type = t.transaction_type;
      if (ADD_TYPES.includes(type)) c.adds++;
      else if (DROP_TYPES.includes(type)) c.drops++;
      else if (type === 'trade') c.trades++;
    });
    return c;
  }, [transactions]);

  const handlePlayerClick = useCallback((mlbId) => {
    if (mlbId) { setActivePlayerId(mlbId.toString()); setIsModalOpen(true); }
  }, []);
  const handleModalClose = () => { setIsModalOpen(false); setTimeout(() => setActivePlayerId(null), 300); };
  const handleExpandToCard = () => { setIsModalOpen(false); setIsCardOpen(true); };
  const handleCollapseToModal = () => { setIsCardOpen(false); setIsModalOpen(true); };
  const handleCloseAll = () => { setIsCardOpen(false); setIsModalOpen(false); setTimeout(() => setActivePlayerId(null), 300); };

  const summaryTiles = [
    { key: 'total',  label: 'Total',  val: counts.total,  color: C.neutral[300] },
    { key: 'adds',   label: 'Adds',   val: counts.adds,   color: C.success },
    { key: 'drops',  label: 'Drops',  val: counts.drops,  color: C.error },
    { key: 'trades', label: 'Trades', val: counts.trades, color: C.primary },
  ];

  return (
    <div style={{ fontVariantNumeric: 'tabular-nums' }}>
      <style>{`.tx-noscroll::-webkit-scrollbar{display:none;}`}</style>

      {/* Summary quick-filter tiles — flush, 1px gaps reveal the border, gold-underline active */}
      <div style={{ display: 'flex', gap: 1, background: C.border, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        {summaryTiles.map(s => {
          const isActive = quickFilter === s.key;
          return (
            <div
              key={s.key}
              onClick={() => setQuickFilter(isActive && s.key !== 'total' ? 'total' : s.key)}
              style={{
                flex: 1, textAlign: 'center', padding: '7px 0', background: C.ground.screen, cursor: 'pointer',
                boxShadow: isActive ? `inset 0 -2px 0 ${C.primary}` : 'none',
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 900, color: s.color, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{s.val}</div>
              <div style={{ fontSize: 8, color: C.muted, textTransform: 'uppercase', letterSpacing: '.3px' }}>{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Filter chip row — Days · Teams · Types · search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 12px', borderBottom: `1px solid ${C.border}` }}>
        <select value={daysFilter} onChange={e => setDaysFilter(Number(e.target.value))} style={CHIP}>
          {DAYS_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)} style={CHIP}>
          <option value="">All teams</option>
          {teamOptions.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <select value={quickFilter} onChange={e => setQuickFilter(e.target.value)} style={CHIP}>
          <option value="total">All types</option>
          <option value="adds">Adds</option>
          <option value="drops">Drops</option>
          <option value="trades">Trades</option>
        </select>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 5, background: C.ground.screen, border: `1px solid ${C.border}`, borderRadius: 3, padding: '4px 9px', color: C.muted }}>
          <SearchIcon />
          <input
            type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search"
            style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none', color: C.textHi, fontSize: 10 }}
          />
        </div>
      </div>

      {/* Flush activity list */}
      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: C.muted, fontSize: 13 }}>Loading transactions…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: C.muted, fontSize: 13 }}>No transactions found</div>
      ) : (
        <div ref={listRef} className="tx-noscroll" style={{ maxHeight: listHeight, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {filtered.map((txn, i) => {
            const config = getConfig(txn.transaction_type);
            const teamName = (txn.to_team_name && txn.to_team_name !== 'Free Agency') ? txn.to_team_name
              : (txn.from_team_name && txn.from_team_name !== 'Free Agency') ? txn.from_team_name : '';
            return (
              <div
                key={txn.transaction_id || i}
                style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', borderBottom: `1px solid ${C.hairline}`, opacity: txn.is_reversed ? 0.4 : 1 }}
              >
                <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: config.color }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: C.coolGray[150], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <b style={{ color: C.textHi }}>{teamName}</b> {config.verb}{' '}
                    <b onClick={() => handlePlayerClick(txn.player_id)} style={{ color: C.primary, cursor: 'pointer' }}>{txn.player_name || 'Unknown'}</b>
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>
                    {formatDate(txn.transaction_date)}
                    {txn.salary != null && ` · $${txn.salary}`}
                    {txn.notes && ` · ${txn.notes.substring(0, 40)}`}
                    {txn.is_reversed && ' · REVERSED'}
                  </div>
                </div>
                <span style={{ fontSize: 8, fontWeight: 800, padding: '2px 5px', borderRadius: 3, color: C.white, background: config.color, flexShrink: 0 }}>
                  {config.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <MobilePlayerModal playerId={activePlayerId} isOpen={isModalOpen} onClose={handleModalClose} onExpandToCard={handleExpandToCard} leagueId={leagueId} />
      <MobilePlayerCard playerId={activePlayerId} leagueId={leagueId} isOpen={isCardOpen} onClose={handleCloseAll} onCollapse={handleCollapseToModal} />
    </div>
  );
}

export default MobileLeagueTransactions;
